import type { ChangeEventHandler } from 'react'
import { useCallback, useMemo } from 'react'

import FilterPanelCheckboxGroup, { type FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import LoyaltySocietePointsFilterGroup from 'loyalty/components/LoyaltySocietePointsFilterGroup'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { queryKeyCampaigns } from 'constants/url'
import { getExperienceSearchBurnCalculationKey } from 'lib/experiences/getExperienceSearchBurnCalculationKey'
import usePrefetchExperienceSearchBurnCalculations from '../hooks/usePrefetchExperienceSearchBurnCalculations'
import { triplePointCampaignId, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import { LUX_PLUS_MONTH_CAMPAIGN_ID, LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import useLoyaltyFilterLabels from 'hooks/Search/useLoyaltyFilterLabels'

interface Props {
  listIds?: Array<string>
}

function ExperienceSearchSocieteFilters({ listIds }: Props) {
  const isBurnEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const currentTakeover = useCurrentSiteTakeover({ vertical: 'experience' })
  const { getLabel: getCampaignFilterLabel } = useLoyaltyFilterLabels()
  usePrefetchExperienceSearchBurnCalculations(listIds)

  const viewPriceInPoints = !!searchFilters.viewPriceInPoints
  const selectedCampaigns = useMemo(() => searchFilters.campaigns ?? [], [searchFilters.campaigns])
  const isTriplePointsSelected = selectedCampaigns.includes(triplePointCampaignId)
  const shouldShowTriplePointsCampaign = currentTakeover?.id === triplePointsTakeoverId
  const isLuxPlusMonthSelected = selectedCampaigns.includes(LUX_PLUS_MONTH_CAMPAIGN_ID)
  const shouldShowLuxPlusMonthCampaign = currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID
  const experienceSearchKey = useMemo(
    () => getExperienceSearchBurnCalculationKey(searchFilters),
    [searchFilters],
  )
  const campaignItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    const items: Array<FilterPanelCheckItem> = []
    if (shouldShowTriplePointsCampaign) {
      items.push({
        value: triplePointCampaignId,
        label: getCampaignFilterLabel(triplePointCampaignId),
        checked: isTriplePointsSelected,
      })
    }
    if (shouldShowLuxPlusMonthCampaign) {
      items.push({
        value: LUX_PLUS_MONTH_CAMPAIGN_ID,
        label: getCampaignFilterLabel(LUX_PLUS_MONTH_CAMPAIGN_ID),
        checked: isLuxPlusMonthSelected,
      })
    }
    return items
  }, [getCampaignFilterLabel, isTriplePointsSelected, shouldShowTriplePointsCampaign, isLuxPlusMonthSelected, shouldShowLuxPlusMonthCampaign])

  const handleViewPriceInPointsChange = useCallback((nextViewPriceInPoints: boolean) => {
    trackSearchFilter({
      label: 'View Price in Points',
      type: 'view_price_in_points',
      value: nextViewPriceInPoints ? 'selected' : 'unselected',
    })
    setSearchFilters({ ...searchFilters, viewPriceInPoints: nextViewPriceInPoints || undefined })
  }, [searchFilters, setSearchFilters, trackSearchFilter])

  const handleCampaignChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    const value = event.target.value
    const nextCampaigns = new Set(selectedCampaigns)
    const campaignLabel = getCampaignFilterLabel(value)

    if (nextCampaigns.has(value)) {
      nextCampaigns.delete(value)
    } else {
      nextCampaigns.add(value)
    }

    trackSearchFilter({
      label: campaignLabel,
      type: 'campaign',
      value: nextCampaigns.has(value) ? 'selected' : 'unselected',
    })
    setSearchFilters({
      ...searchFilters,
      campaigns: nextCampaigns.size > 0 ? Array.from(nextCampaigns) : undefined,
    })
  }, [getCampaignFilterLabel, searchFilters, selectedCampaigns, setSearchFilters, trackSearchFilter])

  if (!isBurnEnabled && campaignItems.length === 0) {
    return null
  }

  return (
    <LoyaltySocietePointsFilterGroup
      compact
      cacheKey={experienceSearchKey}
      skipBatchMaxCap
      skipBurnExperiment
      viewPriceInPoints={viewPriceInPoints}
      onViewPriceInPointsChange={handleViewPriceInPointsChange}
      enableBurnControls={isBurnEnabled}
    >
      {campaignItems.length > 0 && (
        <FilterPanelCheckboxGroup
          name={queryKeyCampaigns}
          items={campaignItems}
          onChange={handleCampaignChange}
        />
      )}
    </LoyaltySocietePointsFilterGroup>
  )
}

export default ExperienceSearchSocieteFilters
